import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import {
  FEEDBACK_PREVIOUS_ROUTES_MAX,
  FEEDBACK_RECENT_ERRORS_MAX,
  FeedbackClientContext,
  FeedbackErrorKindEnum,
  FeedbackRecentError,
} from '@jfw/types';
import { filter } from 'rxjs';
import {
  errorMessageOf,
  parseBrowser,
  parseOs,
  stripQuery,
} from './feedback-client-info';

/**
 * EXT-6172 / EXT-6239: the portal-neutral half of a feedback submission's
 * context -- recent navigation, recent failures, the matched route and client
 * details. Root-provided and constructed at app startup (see each portal's
 * app.config.ts) so history accumulates before the dialog is ever opened.
 */
@Injectable({ providedIn: 'root' })
export class FeedbackActivityRecorder {
  private readonly router = inject(Router);

  // Current route plus the previous FEEDBACK_PREVIOUS_ROUTES_MAX.
  private readonly routeHistory: string[] = [];
  private readonly errors: FeedbackRecentError[] = [];

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.routeHistory.push(stripQuery(event.urlAfterRedirects));
        if (this.routeHistory.length > FEEDBACK_PREVIOUS_ROUTES_MAX + 1) {
          this.routeHistory.shift();
        }
      });
  }

  public recordHttpError(
    method: string,
    url: string,
    status: number,
    message: string,
  ): void {
    this.pushError({
      kind: FeedbackErrorKindEnum.Http,
      at: Date.now(),
      message: message.slice(0, 1000),
      method,
      endpoint: stripQuery(url).slice(0, 500),
      status,
    });
  }

  public recordConsoleError(error: unknown): void {
    this.pushError({
      kind: FeedbackErrorKindEnum.Console,
      at: Date.now(),
      message: errorMessageOf(error),
    });
  }

  public recentErrors(): FeedbackRecentError[] {
    return [...this.errors];
  }

  /** The routes before the current one, newest first. */
  public previousRoutes(): string[] {
    return this.routeHistory.slice(0, -1).reverse();
  }

  public deepestRoute(): ActivatedRouteSnapshot | null {
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    while (route?.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  /** The matched route config, e.g. `/platform-feedback/view/:id`, so
   * submissions can be grouped by page regardless of ids in the URL. */
  public routePattern(): string {
    const segments: string[] = [];
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    while (route) {
      const path = route.routeConfig?.path;
      if (path) {
        segments.push(path);
      }
      route = route.firstChild;
    }
    return `/${segments.join('/')}`;
  }

  public clientInfo(): FeedbackClientContext['client'] {
    const userAgent = navigator.userAgent;
    return {
      browser: parseBrowser(userAgent),
      os: parseOs(userAgent),
      viewport: { width: window.innerWidth, height: window.innerHeight },
      screen: { width: window.screen.width, height: window.screen.height },
      devicePixelRatio: window.devicePixelRatio || 1,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
      language: navigator.language ?? '',
    };
  }

  private pushError(error: FeedbackRecentError): void {
    this.errors.push(error);
    if (this.errors.length > FEEDBACK_RECENT_ERRORS_MAX) {
      this.errors.shift();
    }
  }
}
